import React from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import { useSeo } from "../lib/seo";
import { CreditCard, Smartphone, Landmark, CalendarClock, Wallet, Lock } from "lucide-react";

const SECTIONS = [
  {
    id: "cards",
    icon: CreditCard,
    title: "Credit & Debit Cards",
    body: [
      "We accept major credit and debit cards through our secure checkout. Available card brands are displayed during checkout.",
    ],
  },
  {
    id: "wallets",
    icon: Smartphone,
    title: "Apple Pay & Google Pay",
    body: [
      "Eligible customers can use Apple Pay or Google Pay for a fast and secure checkout experience. Availability may depend on your device, browser, and payment eligibility.",
    ],
  },
  {
    id: "ach",
    icon: Landmark,
    title: "US Bank Account (ACH)",
    emphasis: "Pay directly from your US bank account",
    body: [
      "Lecis Auto Glass customers may pay directly from an eligible US bank account through our secure Stripe checkout. Select \u201CUS bank account\u201D at checkout and follow the instructions to securely connect or verify your bank account.",
      "Bank payments may take additional time to process and clear. An order paid by bank account is not considered fully paid until the payment has been successfully confirmed. Order processing and fulfillment are subject to successful payment confirmation.",
    ],
  },
  {
    id: "klarna",
    icon: CalendarClock,
    title: "Klarna",
    body: [
      "Klarna may be available for eligible purchases. If available for your order, Klarna will appear as a payment option during checkout. Eligibility and payment terms are determined by Klarna.",
    ],
  },
  {
    id: "other",
    icon: Wallet,
    title: "Other Eligible Payment Options",
    body: [
      "Additional payment methods may be displayed during checkout depending on your device, location, order, and payment eligibility.",
    ],
  },
  {
    id: "secure",
    icon: Lock,
    title: "Secure checkout powered by Stripe",
    body: [
      "Lecis Auto Glass uses Stripe to securely process online payments. Available payment methods may vary depending on your order, device, location, and payment eligibility.",
      "Lecis Auto Glass does not require customers to send credit card or online banking credentials by email.",
    ],
  },
];

export default function PaymentOptions() {
  useSeo({
    title: "Payment Options | Lecis Auto Glass",
    description:
      "Learn about secure payment options available at Lecis Auto Glass, including major credit and debit cards, digital wallets, and US bank account payments.",
    canonicalPath: "/payment-options",
  });

  return (
    <StorefrontLayout>
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="payment-options-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-red-500 font-bold mb-6">
          Payment
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mb-6">
          Payment Options
        </h1>
        <p className="text-zinc-400 text-base md:text-lg leading-relaxed mb-12">
          Flexible, secure ways to pay for your Lecis Auto Glass order.
        </p>

        <div className="space-y-5">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <section
                key={s.id}
                className="border border-white/10 rounded-md p-6"
                data-testid={`payment-section-${s.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Icon size={18} className="text-red-500 shrink-0" aria-hidden="true" />
                  <h2 className="font-display text-xl text-zinc-100">{s.title}</h2>
                </div>
                {s.emphasis && (
                  <p className="text-sm font-medium text-zinc-200 mb-2">{s.emphasis}</p>
                )}
                {s.body.map((p, i) => (
                  <p key={i} className="text-sm text-zinc-400 leading-relaxed mb-2 last:mb-0">
                    {p}
                  </p>
                ))}
              </section>
            );
          })}
        </div>

        <p className="text-[11px] text-zinc-600 leading-relaxed mt-10">
          Payment options are shown at checkout based on eligibility. Questions about an order?{" "}
          <Link to="/contact" className="text-zinc-400 hover:text-zinc-200 underline underline-offset-2" data-testid="payment-options-contact-link">
            Contact us
          </Link>
          .
        </p>
      </div>
    </StorefrontLayout>
  );
}
